/**
 * @file FormSection.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Component of courses to manage courses
 */
import React from "react";
import {
  Divider,
  Stack,
  TextField,
  Typography,
  Skeleton,
  Box,
  Switch,
  Autocomplete,
} from "@mui/material";

export const FormSection = ({
  isLoading,
  categories,
  register,
  errors,
  setValue,
  watch,
}) => {
  const isActive = watch("isactive");

  const handleSwitchChange = (event) => {
    setValue("isactive", event.target.checked ? 1 : 0);
  };

  return (
    <Box
      flex={1}
      height="100%"
      borderRadius="12px"
      px="8px"
      py={3}
      bgcolor="#F7F7F7"
      display="flex"
      flexDirection="column"
    >
      <Stack
        pb={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isLoading ? (
          <Skeleton variant="rounded" width={140} height={24} />
        ) : (
          <Typography fontWeight={500} fontSize={20} color="#222222">
            Enter details
          </Typography>
        )}
      </Stack>
      <Divider />
      <Box overflow="auto" pt={2}>
        {/* Course Category Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Choose Course Category</Typography>
            <Autocomplete
              size="small"
              options={categories}
              getOptionLabel={(option) => option?.category}
              value={
                categories?.find((c) => c.id === watch("category")) || null
              }
              onChange={(event, newValue) => {
                setValue("category", newValue?.id || null);
              }}
              sx={{
                minWidth: 206,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select category"
                  error={!!errors.category}
                  helperText={errors.category ? errors.category.message : ""}
                />
              )}
            />
          </Stack>
        )}

        {/* Course Name Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Course Name</Typography>
            <Box width={206}>
              <TextField
                placeholder="Enter course name"
                sx={{
                  bgcolor: "transparent",
                  my: 1,
                  "& .MuiFormControl-root": {
                    width: "100%",
                    m: 0,
                  },
                  "& .MuiInputBase-input": {
                    mx: 0,
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: 44,
                  },
                }}
                error={!!errors.coursename}
                helperText={errors.coursename ? errors.coursename.message : ""}
                {...register("coursename", {
                  required: "Course name is required",
                })}
              />
            </Box>
          </Stack>
        )}

        {/* isActive Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Is Active ?</Typography>

            <Switch
              checked={isActive === 1}
              onChange={handleSwitchChange}
              inputProps={{ "aria-label": "controlled" }}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );
};
